import {
  CSSProperties,
  FC,
  PropsWithChildren,
  ReactNode,
  useContext,
  useState,
} from "react";
import AppTheme from "@shared/common/Themes/AppTheme";
import { ThemeContext } from "styled-components";
import { TooltipPlacement } from "antd/es/tooltip";
import styled from "styled-components";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import Text from "common/Typography/Text";
import Tooltip from "common/Tooltips/Tooltip";

export interface ActionTooltipProps {
  title: ReactNode;
  body?: string;
  placement?: TooltipPlacement;
  onClick?: () => void;
  hideMobile?: boolean;
  containerStyle?: CSSProperties;
  maxWidth?: number;
  onOpenChanged?: (isVisible: boolean) => void;
  tooltipStyle?: CSSProperties;
  children?: JSX.Element;
}

export const ActionTooltip: FC<PropsWithChildren<ActionTooltipProps>> = ({
  body,
  title,
  placement,
  onClick,
  hideMobile = false,
  containerStyle,
  children,
  maxWidth,
  onOpenChanged,
  tooltipStyle,
}) => {
  const appTheme = useContext<typeof AppTheme>(ThemeContext);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const shouldShowTooltip = !isMobile || !hideMobile;
  const [tooltipVisible, setTooltipVisible] = useState(false);
  maxWidth = maxWidth || (isMobile ? 250 : 350);

  return (
    <div
      style={containerStyle}
      onClick={(e) => {
        e.stopPropagation();
        if (!isMobile) onClick?.();
      }}
    >
      {shouldShowTooltip ? (
        <Tooltip
          styles={{
            body: {
              padding: 12,
              ...tooltipStyle,
            },
          }}
          placement={placement || "bottom"}
          open={tooltipVisible}
          onOpenChange={(newVisibleState) => {
            onOpenChanged?.(newVisibleState);
            setTooltipVisible(newVisibleState);
          }}
          showMobileBottomDrawer={true}
          title={
            <TooltipWrapper
              $hasClickHandler={Boolean(onClick)}
              $maxWidth={maxWidth}
              onClick={(e) => {
                e.stopPropagation();
                onClick?.();
                if (onClick) {
                  setTooltipVisible(false);
                }
              }}
            >
              <StyledText
                as="div"
                style={{
                  fontWeight: body
                    ? appTheme.typography.fontWeights.MEDIUM
                    : appTheme.typography.fontWeights.NORMAL,
                }}
              >
                {title}
              </StyledText>
              {body && (
                <StyledText
                  as="div"
                  type="secondary"
                  style={{ color: "#bdc1c6" }}
                >
                  {body}
                </StyledText>
              )}
            </TooltipWrapper>
          }
        >
          {children}
        </Tooltip>
      ) : (
        children
      )}
    </div>
  );
};

const TooltipWrapper = styled.div<{
  $hasClickHandler: boolean;
  $maxWidth: number;
}>`
  cursor: ${(p) => (p.$hasClickHandler ? "pointer" : "initial")};
  max-width: ${({ $maxWidth }) => $maxWidth}px;
`;

const StyledText = styled(Text)`
  color: ${(p) => p.theme.colors.white};
`;
